import type { KeyboardEventHandler, Ref } from 'react';
import React from 'react';
import type { AnyMessage, FileMessage, UserState } from '../chatTypes';
import { isFileMessage, isNoticeMessage, isTextMessage } from '../chatTypes';
import ChatTextMessage from './ChatTextMessage';
import ChatNoticeMessage from './ChatNoticeMessage';
import ChatFileMessage from './ChatFileMessage';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';
import type { ValidIconName } from '../../basic';
import type { ISendEventResponse } from 'matrix-js-sdk';

export type ChatMessageComponentProps = {
  message: AnyMessage;
  tabIndex?: number;
  messageRef?: Ref<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  /** Moves the focus out of the message list, see {@link ChatTextMessage}. */
  onEscape?: () => boolean;
  emojiData?: EmojiData;
  reactionClick: (
    eventId: string,
    reactionKey: string,
    roomId?: string | undefined
  ) => Promise<ISendEventResponse | undefined>;
  roomId?: string;
  userState?: UserState;
  /** Disables adding/toggling reactions, see {@link ChatTextMessage}. */
  reactionsDisabled?: boolean;
  /** Called when the download button of a file message is clicked. */
  onFileDownload?: (message: FileMessage) => Promise<void> | void;
};

export default function ChatMessageComponent({
  message,
  tabIndex,
  messageRef,
  onKeyDown,
  onFocus,
  onEscape,
  emojiData,
  reactionClick,
  roomId,
  userState,
  reactionsDisabled,
  onFileDownload
}: Readonly<ChatMessageComponentProps>): React.JSX.Element {
  if (isTextMessage(message)) {
    return (
      <ChatTextMessage
        person={message.user}
        date={message.date}
        text={message.content.text}
        isUndecryptable={message.content.isUndecryptable}
        errorText={message.error}
        status={message.status}
        tabIndex={tabIndex}
        messageRef={messageRef}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onEscape={onEscape}
        emojiData={emojiData}
        reactionClick={reactionClick}
        eventId={message.id}
        roomId={message.roomId ?? roomId}
        reactions={message.reactions}
        userState={userState}
        reactionsDisabled={reactionsDisabled}
      />
    );
  }

  if (isNoticeMessage(message)) {
    return (
      <ChatNoticeMessage
        text={message.content.text}
        date={message.date}
        icon={message.content.icon as ValidIconName | undefined}
        noticeType={message.content.noticeType}
        tabIndex={tabIndex}
        messageRef={messageRef}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
      />
    );
  }

  if (isFileMessage(message)) {
    return (
      <ChatFileMessage
        person={message.user}
        date={message.date}
        fileName={message.content.fileName}
        mimeType={message.content.mimeType}
        size={message.content.size}
        onDownload={
          onFileDownload && message.content.loadFile
            ? () => onFileDownload(message)
            : undefined
        }
        errorText={message.error}
        status={message.status}
        tabIndex={tabIndex}
        messageRef={messageRef}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onEscape={onEscape}
        emojiData={emojiData}
        reactionClick={reactionClick}
        eventId={message.id}
        roomId={message.roomId ?? roomId}
        reactions={message.reactions}
        userState={userState}
        reactionsDisabled={reactionsDisabled}
      />
    );
  }

  // should never happen
  return <>Unknown message type</>;
}
